import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { ProfessorStudentGroupService } from '../../../../core/services/groups/professor-student-group.service';
import { ProfessorTestAssignmentService } from '../../../../core/services/assignments/professor-test-assignment.service';
import { StudentService } from '../../../../core/services/student.service';
import { SwalService } from '../../../../core/services/utils/swal.service';

import {
  CreateTestAssignmentDto,
  TestAssignment,
  TestAssignmentTarget,
} from '../../../../core/interfaces/test-assignment';
import { StudentGroup } from '../../../../core/interfaces/student-group';
import { ErrorStateComponent } from '../../error-state/error-state.component';

interface StudentLite { id: number; fullName: string; code?: string | null; }

@Component({
  selector: 'app-assign-test-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ErrorStateComponent],
  templateUrl: './assign-test-modal.component.html',
  styleUrl: './assign-test-modal.component.css',
})
export class AssignTestModalComponent implements OnInit {
  @Input({ required: true }) testId!: number;
  @Input() testTitle?: string;
  @Input() subjectId?: number | null;
  @Input() subjectName?: string | null;

  @Output() closed = new EventEmitter<void>();
  @Output() created = new EventEmitter<TestAssignment>();

  private readonly assignmentService = inject(ProfessorTestAssignmentService);
  private readonly groupService = inject(ProfessorStudentGroupService);
  private readonly studentService = inject(StudentService);
  private readonly swal = inject(SwalService);

  readonly target = signal<TestAssignmentTarget>('group');
  readonly groupId = signal<number | null>(null);
  readonly studentId = signal<number | null>(null);
  readonly studentQuery = signal<string>('');
  readonly instructions = signal<string>('');
  readonly dueDate = signal<string>(''); // yyyy-MM-dd

  readonly groups = signal<StudentGroup[]>([]);
  readonly students = signal<StudentLite[]>([]);
  readonly submitting = signal<boolean>(false);

  readonly groupsLoading = signal<boolean>(false);
  readonly groupsErrored = signal<boolean>(false);
  readonly studentsLoading = signal<boolean>(false);
  readonly studentsErrored = signal<boolean>(false);

  readonly filteredStudents = computed(() => {
    const q = this.studentQuery().trim().toLowerCase();
    if (!q) return this.students().slice(0, 30);
    return this.students()
      .filter((s) => `${s.fullName} ${s.code ?? ''}`.toLowerCase().includes(q))
      .slice(0, 30);
  });

  /**
   * Les cibles nécessaires au type sélectionné n'ont pas pu être chargées :
   * on ne peut pas proposer de valider une affectation sans cible réelle.
   */
  readonly targetsErrored = computed(() => {
    const t = this.target();
    if (t === 'group') return this.groupsErrored();
    if (t === 'student') return this.studentsErrored();
    return false;
  });

  readonly canSubmit = computed(
    () => !this.submitting() && !this.targetsErrored(),
  );

  ngOnInit(): void {
    this.loadGroups();
    this.loadStudents();
  }

  setTarget(t: TestAssignmentTarget): void {
    this.target.set(t);
  }

  pickStudent(id: number): void {
    this.studentId.set(id);
  }

  cancel(): void {
    this.closed.emit();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    const target = this.target();
    let valid = true;
    if (target === 'group' && !this.groupId()) valid = false;
    if (target === 'student' && !this.studentId()) valid = false;
    if (target === 'subject' && !this.subjectId) valid = false;
    if (!valid) {
      this.swal.warning('ASSIGNMENTS.FORM.PICK_TARGET');
      return;
    }

    const payload: CreateTestAssignmentDto = {
      testId: this.testId,
      targetType: target,
      groupId: target === 'group' ? this.groupId() : null,
      studentId: target === 'student' ? this.studentId() : null,
      subjectId: target === 'subject' ? (this.subjectId ?? null) : null,
      instructions: this.instructions().trim() || null,
      dueDate: this.dueDate() ? new Date(this.dueDate()).toISOString() : null,
    };

    this.submitting.set(true);
    this.assignmentService.create(payload).subscribe({
      next: (res) => {
        this.submitting.set(false);
        if (res?.data) {
          this.swal.success('ASSIGNMENTS.FORM.CREATED');
          this.created.emit(res.data);
          this.closed.emit();
        }
      },
      error: (err) => {
        console.error('Create assignment failed:', err);
        this.submitting.set(false);
        this.swal.error('ASSIGNMENTS.FORM.CREATE_ERROR');
      },
    });
  }

  // ─────────────────────────────────────────────────────────
  /** Rechargement manuel des groupes, déclenché par <app-error-state>. */
  reloadGroups(): void {
    this.loadGroups();
  }

  /** Rechargement manuel des élèves, déclenché par <app-error-state>. */
  reloadStudents(): void {
    this.loadStudents();
  }

  private loadGroups(): void {
    this.groupsLoading.set(true);
    this.groupsErrored.set(false);
    this.groupService.myGroups().subscribe({
      next: (res) => {
        const list = res?.data ?? [];
        this.groups.set(list);
        if (list.length > 0 && this.groupId() == null) {
          this.groupId.set(list[0].id);
        }
        this.groupsLoading.set(false);
      },
      error: () => {
        this.groups.set([]);
        this.groupId.set(null);
        this.groupsErrored.set(true);
        this.groupsLoading.set(false);
      },
    });
  }

  private loadStudents(): void {
    this.studentsLoading.set(true);
    this.studentsErrored.set(false);
    this.studentService.getStudents(undefined, true).subscribe({
      next: (res: any) => {
        const list = (res?.data ?? []).map((s: any): StudentLite => ({
          id: s.id,
          fullName: `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || `#${s.id}`,
          code: s.studentCode ?? null,
        }));
        this.students.set(list);
        this.studentsLoading.set(false);
      },
      error: () => {
        this.students.set([]);
        this.studentId.set(null);
        this.studentsErrored.set(true);
        this.studentsLoading.set(false);
      },
    });
  }
}
